import { DeepPartial } from 'react-hook-form';
import { PaymentMethod } from '../../offer-checkout/constants/payment-method.enum';
import { OfferCheckoutViewData } from '../../offer-checkout/components/offer-checkout-view/offer-checkout-view';
import { BillingTerm } from '../../products/constants/billing-term.enum';
import { parseEuroInputToCents } from '../../products/lib/money';
import { OfferFormValues } from '../components/offer-form/offer-form.schema';
import { OfferAddOnOptionResponseDto, OfferMetaResponseDto, OfferProductOptionResponseDto, OfferResponseDto } from '../types/response';

const orNull = (value: string | undefined): string | null => value?.trim() || null;

/** Same rule as the backend for the checkout: the next few dates, plus a later prefilled one so the prefill is never lost. */
const toCustomerStartDateOptions = ({ startDateOptions, customerStartDateCount }: OfferMetaResponseDto, prefilled: string | null): string[] => {
  const options = startDateOptions.slice(0, customerStartDateCount);
  return prefilled && startDateOptions.includes(prefilled) && !options.includes(prefilled) ? [...options, prefilled] : options;
};

/**
 * What the customer would see for the current form values – same shape as `POST offers/checkout/view`.
 * An unfinished price shows as 0 and an unpicked payment method as invoice, so the preview never breaks while typing.
 */
export const toOfferPreviewData = (
  values: DeepPartial<OfferFormValues>,
  product: OfferProductOptionResponseDto,
  term: BillingTerm,
  addOns: OfferAddOnOptionResponseDto[],
  meta: OfferMetaResponseDto,
  offerFileName: string | null
): OfferCheckoutViewData => {
  const customer = values.customer ?? {};
  const pickedAddOns = (values.addOnIds ?? []).flatMap((id) => addOns.filter((addOn) => addOn._id === id));

  return {
    product: {
      title: values.title ?? '',
      description: values.description ?? '',
      capacity: product.capacity,
      term,
      monthlyPriceCents: parseEuroInputToCents(values.monthlyPrice ?? '') ?? 0,
      additionalConditions: orNull(values.additionalConditions)
    },
    addOns: pickedAddOns.map(({ title, description }) => ({ title, description })),
    customer: {
      mail: customer.mail?.trim().toLowerCase() ?? '',
      firstName: orNull(customer.firstName),
      lastName: orNull(customer.lastName),
      name: orNull(customer.name),
      vatId: orNull(customer.vatId),
      street: orNull(customer.street),
      houseNumber: orNull(customer.houseNumber),
      postCode: orNull(customer.postCode),
      city: orNull(customer.city),
      country: orNull(customer.country),
      invoiceMail: orNull(customer.invoiceMail),
      phone: orNull(customer.phone),
      newCustomerDate: orNull(customer.newCustomerDate)
    },
    paymentMethod: values.paymentMethod ?? PaymentMethod.INVOICE,
    offerFileName,
    startDateOptions: toCustomerStartDateOptions(meta, orNull(customer.newCustomerDate))
  };
};

/** What the customer sees for a saved offer – the stored snapshot, with today's start dates like `POST offers/checkout/view`. */
export const toSavedOfferPreviewData = (offer: OfferResponseDto, meta: OfferMetaResponseDto): OfferCheckoutViewData => {
  const { iban, bic, ...customer } = offer.customer;

  return {
    product: offer.product,
    addOns: offer.addOns.map(({ title, description }) => ({ title, description })),
    customer,
    paymentMethod: offer.paymentMethod,
    offerFileName: offer.offerFile?.fileName ?? null,
    startDateOptions: toCustomerStartDateOptions(meta, offer.customer.newCustomerDate)
  };
};
